/**
 * ★ THE ARTICLE PREVIEW (the owner, 2026-10-20: a preview before the article is sent, showing how it
 * looks in the mobile app and as a web page on the site). "Preview" beside "Submit for review" opens
 * the draft on screen - unsaved edits included; looking saves and sends nothing - drawn the way the
 * MoonX app and the website draw an article, switched by "App / Website", in the cabinet's own
 * scheme (its display settings switch dark and light), with the way back to editing and Submit.
 *
 * A COPY OF TWO OTHER PRODUCTS' DESIGN, as the profile preview is (design 24, T28), at the same
 * cost: when either changes, this follows. Each side draws only what that client shows today.
 *   - The app (MoonX_iOS develop, `Screens/Article`, the reading layout of 2026-10-13): no cover;
 *     "Member-only story", the topic pill, the title, the subtitle, "N min read · date", the byline
 *     with Follow; the body in the serif face, pictures at their own proportions with the caption
 *     centred under them, "• • •" between parts; the topic again, "Written by" and Comments.
 *   - The website (MoonX_WebSite main, `magazine/[category]/[slug]/page.tsx`,
 *     `MagazineArticleBody`, `ArticleMarkdown`; measured on moonx.app, 2026-10-20): the moon ticker
 *     and the header, the breadcrumbs, the category over the title, the author card with the short
 *     bio and Follow, the meta bar with the reader actions, the body in its 760px column - headings
 *     at their levels, pictures at their own proportions, no cover and no caption - and Responses.
 * The author's name and photo are their profile's (`getOwnProfile`, the byline card's read), and the
 * date is the planned one, or else the draft's last save - the cabinet reads no wall clock (C11).
 * Both clients' own words ("Follow", "Written by"...) are drawn in the cabinet's language.
 */
import { Alert, Button, Checkbox, Modal, SegmentedControl, Text } from '@mantine/core';
import {
  ArrowLeft,
  Bookmark,
  ChevronLeft,
  Eye,
  MessageCircle,
  Moon,
  Plus,
  Send,
  Settings,
} from '@moonx/ui';
import { useCallback, useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import logoUrl from '../../../assets/moonx-logo.png';
import { useEditorApi } from '../../../features/cabinet/api/editor-api';
import type { Block, EditorCategory, OwnProfile } from '../../../features/cabinet/api/editor-api';
import { articleWordCount } from '../../../features/cabinet/api/editor-measure';
import { CABINET_LOCALE } from '../../../features/cabinet/format';
import { useAsync } from '../../../features/cabinet/state';
import {
  emulatorImageUrl,
  servedUrlOnError,
  useServedImageSrc,
} from '../../../features/storage/emulator-image-url';
import { useTranslation } from '../../../i18n';
import { BlockVideoPlayer } from './VideoBlockPlayer';
import { ReadOnlyText } from './ReadOnlyText';
import type { ReadOnlyTextClasses } from './ReadOnlyText';
import type { EditorDraft } from './document';
import classes from './article-preview.module.css';

export type PreviewSurface = 'app' | 'website';

export interface ArticlePreviewProps {
  /** The article a video block is read from (0144). */
  readonly articleId: string;
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly draft: EditorDraft;
  /** The catalogue, for the category's name. */
  readonly categories: readonly EditorCategory[];
  /** When the draft was last saved - the date drawn while no publication date is planned. */
  readonly savedAt: string;
  /**
   * Submit from the preview - its words, its action, and why it is held (a picture still going
   * in, as the editor's own Submit is held), else null; absent while sending waits for approval.
   */
  readonly submit:
    | { readonly label: string; readonly run: () => void; readonly heldReason: string | null }
    | undefined;
}

export function ArticlePreview({
  articleId,
  opened,
  onClose,
  draft,
  categories,
  savedAt,
  submit,
}: ArticlePreviewProps) {
  const { t } = useTranslation('articles');
  const lineId = useId();
  const heldId = useId();
  const [surface, setSurface] = useState<PreviewSurface>('app');
  // The last read before sending (the owner, 2 November 2026): the tick sits under the article, so it
  // cannot be reached without going through it, and it is asked again each time the window opens.
  const [reread, setReread] = useState(false);
  useEffect(() => {
    if (opened) {
      setReread(false);
    }
  }, [opened]);
  const heldReason =
    submit === undefined
      ? null
      : (submit.heldReason ?? (reread ? null : t('editor.previewWindow.finalHeld')));
  const held = heldReason !== null;

  return (
    <Modal.Root opened={opened} onClose={onClose} fullScreen>
      <Modal.Overlay />
      <Modal.Content aria-labelledby={lineId} className={classes.window}>
        <Modal.Body className={classes.windowBody}>
          <div className={classes.bar}>
            <div className={classes.barText}>
              <Eye size={16} aria-hidden="true" className={classes.barMark} />
              <Text id={lineId} className={classes.barLine}>
                {t('editor.previewWindow.line')}
              </Text>
            </div>
            <div className={classes.barControls}>
              <SegmentedControl
                aria-label={t('editor.previewWindow.showAs')}
                radius="xl"
                size="sm"
                value={surface}
                onChange={(value) => {
                  setSurface(value === 'website' ? 'website' : 'app');
                }}
                data={[
                  { value: 'app', label: t('editor.previewWindow.surfaceApp') },
                  { value: 'website', label: t('editor.previewWindow.surfaceWebsite') },
                ]}
              />
            </div>
            <div className={classes.barActions}>
              {/* Always mounted, even empty: a live region that appears with its message may never
                  be announced (the editor's own held line says the same). */}
              <Text id={heldId} role="status" className={classes.barHeld}>
                {heldReason ?? ''}
              </Text>
              <Button
                variant="default"
                radius="xl"
                size="sm"
                leftSection={<ArrowLeft size={14} aria-hidden="true" />}
                onClick={onClose}
              >
                {t('editor.previewWindow.back')}
              </Button>
              {submit === undefined ? null : (
                <Button
                  radius="xl"
                  size="sm"
                  leftSection={<Send size={14} aria-hidden="true" />}
                  // Held, not disabled, as the editor's Submit: focusable, so its reason is reachable.
                  data-disabled={held}
                  aria-disabled={held || undefined}
                  aria-describedby={held ? heldId : undefined}
                  onClick={(event) => {
                    if (held) {
                      event.preventDefault();
                      return;
                    }
                    submit.run();
                  }}
                >
                  {submit.label}
                </Button>
              )}
            </div>
          </div>

          <div className={classes.stage}>
            <div className={classes.stageColumn}>
              {submit === undefined ? null : (
                <Alert variant="light" className={classes.finalCheck}>
                  {t('editor.previewWindow.finalNote')}
                </Alert>
              )}
              <PreviewOf
                surface={surface}
                articleId={articleId}
                draft={draft}
                categories={categories}
                savedAt={savedAt}
              />
              {submit === undefined ? null : (
                <Checkbox
                  className={classes.finalCheck}
                  label={t('editor.previewWindow.finalTick')}
                  checked={reread}
                  onChange={(event) => {
                    setReread(event.currentTarget.checked);
                  }}
                />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

/* ------------------------------------------------------------------------- the article itself */

/** Everything both sides draw, read once. */
interface PreviewArticle {
  readonly articleId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly category: string | null;
  readonly authorName: string;
  readonly authorHeadline: string | null;
  readonly authorAbout: string | null;
  readonly avatar: { readonly src: string | null; readonly onError: () => void };
  readonly words: number;
  readonly date: Date;
  readonly blocks: readonly Block[];
}

function PreviewOf({
  articleId,
  surface,
  draft,
  categories,
  savedAt,
}: {
  readonly articleId: string;
  readonly surface: PreviewSurface;
  readonly draft: EditorDraft;
  readonly categories: readonly EditorCategory[];
  readonly savedAt: string;
}) {
  const api = useEditorApi();
  // The byline card's read (./AboutThisPiece.tsx), by its key, so the profile is read once.
  const profile = useAsync<OwnProfile>(
    useCallback((signal: AbortSignal) => api.getOwnProfile(signal), [api]),
    'editor:own-profile',
  );
  const avatar = useServedImageSrc(profile.data?.avatarUrl ?? null);
  const categoryCode = draft.addressCategory ?? draft.categories[0] ?? null;
  // The headline only while it is public, as the reader's page has it; the short bio has no switch.
  const headline =
    profile.data?.visibility.headline === false ? '' : (profile.data?.headline?.trim() ?? '');
  const about = profile.data?.bioShort?.trim() ?? '';
  const article: PreviewArticle = {
    articleId,
    title: draft.title.trim(),
    subtitle: draft.subtitle.trim(),
    category:
      categoryCode === null
        ? null
        : (categories.find((item) => item.code === categoryCode)?.label ?? null),
    authorName: profile.data?.displayName?.trim() ?? '',
    authorHeadline: headline === '' ? null : headline,
    authorAbout: about === '' ? null : about,
    avatar,
    words: articleWordCount(draft),
    date: new Date(draft.plannedGoLiveAt ?? savedAt),
    blocks: draft.blocks,
  };
  return surface === 'app' ? <AppArticle article={article} /> : <WebArticle article={article} />;
}

/** The author's photo, or their initial while there is none. */
function AuthorPhoto({
  article,
  className,
}: {
  readonly article: PreviewArticle;
  readonly className: string | undefined;
}) {
  return article.avatar.src === null ? (
    <span className={className} aria-hidden="true">
      {article.authorName.slice(0, 1).toUpperCase() || '?'}
    </span>
  ) : (
    <img className={className} src={article.avatar.src} onError={article.avatar.onError} alt="" />
  );
}

/** A body picture: the served rendition, or the emulator's while it serves the local stack. */
function BodyImage({
  block,
  className,
}: {
  readonly block: Extract<Block, { kind: 'image' }>;
  readonly className: string | undefined;
}) {
  if (block.url === undefined) {
    return null;
  }
  return (
    <img
      className={className}
      src={emulatorImageUrl(block.url)}
      onError={servedUrlOnError(block.url)}
      alt={block.imageAlt ?? ''}
    />
  );
}

/** Scattered dots, fixed so they never move between looks: the app's and the site's starfields. */
function starsFor(width: number, height: number, count: number) {
  let seed = 7;
  const next = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  return Array.from({ length: count }, () => ({
    x: Math.round(next() * width * 10) / 10,
    y: Math.round(next() * height * 10) / 10,
    r: next() < 0.25 ? 1.2 : 0.7,
  }));
}

function Starfield({
  className,
  width,
  height,
  count,
}: {
  readonly className: string | undefined;
  readonly width: number;
  readonly height: number;
  readonly count: number;
}) {
  return (
    <svg
      className={className}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      preserveAspectRatio="xMidYMin slice"
      aria-hidden="true"
    >
      {starsFor(width, height, count).map((star, index) => (
        <circle key={index} cx={star.x} cy={star.y} r={star.r} />
      ))}
    </svg>
  );
}

/** Both clients' divider: a four-point sparkle between two dots (44 x 18). */
function SparkleDivider({ className }: { readonly className: string | undefined }) {
  return (
    <div className={className} aria-hidden="true">
      <svg width="44" height="18" viewBox="0 0 44 18" fill="currentColor">
        <path d="M22.5 0C23.824 6.46152 24.89753 7.59825 31 9C24.89753 10.402109 23.824 11.53848 22.5 18C21.176 11.53848 20.10247 10.402109 14 9C20.10247 7.59825 21.176 6.46152 22.5 0Z" />
        <circle cx="2" cy="9" r="2" />
        <circle cx="42" cy="9" r="2" />
      </svg>
    </div>
  );
}

/* ----------------------------------------------------------------------------- the website */

const WEB_TEXT: ReadOnlyTextClasses = {
  text: classes.webText,
  quote: classes.webQuote,
  quoteText: classes.webQuoteText,
  attribution: classes.webAttribution,
};

/** The website's date and time: "Oct 20, 2026 · 14:05", as its meta bar writes them. */
function webDate(date: Date): string {
  const day = new Intl.DateTimeFormat(CABINET_LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
  const time = new Intl.DateTimeFormat(CABINET_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
  return `${day} · ${time}`;
}

/** The website's reading time: the text blocks' words / 200, rounded, at least one. */
function webMinutes(blocks: readonly Block[]): number {
  const words = blocks.reduce(
    (sum, block) =>
      sum + (block.kind === 'text' ? block.body.split(/\s+/).filter(Boolean).length : 0),
    0,
  );
  return Math.max(1, Math.round(words / 200));
}

/** A stroke icon the UI kit does not carry, drawn as the site's (lucide) one. */
function Glyph({ name, size }: { readonly name: 'phone' | 'share'; readonly size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {name === 'phone' ? (
        <>
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <path d="M12 18h.01" />
        </>
      ) : (
        <>
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
          <path d="m16 6-4-4-4 4" />
          <path d="M12 2v13" />
        </>
      )}
    </svg>
  );
}

function WebArticle({ article }: { readonly article: PreviewArticle }) {
  const { t } = useTranslation('articles');
  const title = article.title === '' ? t('common.untitled') : article.title;
  return (
    <section className={classes.browser} aria-label={t('editor.previewWindow.onWebsite')}>
      <div className={classes.browserTop} aria-hidden="true">
        <span className={classes.browserDots}>
          <i />
          <i />
          <i />
        </span>
        <span className={classes.browserAddress}>moonx.app</span>
      </div>
      <div className={classes.web}>
        <Starfield className={classes.webStars} width={1200} height={1600} count={160} />
        <div className={classes.webTicker} aria-hidden="true">
          <span className={classes.webTickerDot} />
          {t('editor.previewWindow.web.today')} <i>·</i> {t('editor.previewWindow.web.moonPhase')}{' '}
          <i>·</i> {t('editor.previewWindow.web.lunarDay')} <i>·</i>{' '}
          {t('editor.previewWindow.web.moonSign')} <i>·</i>{' '}
          {t('editor.previewWindow.web.illumination')}
        </div>
        <div className={classes.webHeader} aria-hidden="true">
          <span className={classes.webMenu} />
          <span
            className={classes.webLogo}
            style={{ maskImage: `url(${logoUrl})`, WebkitMaskImage: `url(${logoUrl})` }}
          />
          <span className={classes.webEnd}>
            <span className={classes.webGear}>
              <Settings size={16} />
            </span>
            <span className={classes.webGetApp}>
              <Glyph name="phone" size={16} />
              <span className={classes.webGetAppWords}>{t('editor.previewWindow.web.getApp')}</span>
            </span>
            <span className={classes.webSignIn}>{t('editor.previewWindow.web.signIn')}</span>
          </span>
        </div>

        <article className={classes.webColumn}>
          <p className={classes.webCrumbs}>
            {t('editor.previewWindow.web.home')} <span>›</span>{' '}
            {t('editor.previewWindow.web.magazine')}{' '}
            {article.category === null ? null : (
              <>
                <span>›</span> {article.category}{' '}
              </>
            )}
            <span>›</span> {title}
          </p>
          {article.category === null ? null : (
            <p className={classes.webEyebrow}>{article.category}</p>
          )}
          <h1 className={classes.webTitle}>{title}</h1>
          {article.subtitle === '' ? null : <p className={classes.webDek}>{article.subtitle}</p>}

          <div className={classes.webAbout}>
            <div className={classes.webAuthor}>
              <div className={classes.webAuthorWho}>
                <AuthorPhoto article={article} className={classes.webAvatar} />
                <div className={classes.webAuthorText}>
                  <p className={classes.webWrittenBy}>{t('editor.previewWindow.web.writtenBy')}</p>
                  <p className={classes.webAuthorName}>{article.authorName}</p>
                  {article.authorHeadline === null ? null : (
                    <p className={classes.webAuthorTitle}>{article.authorHeadline}</p>
                  )}
                  {article.authorAbout === null ? null : (
                    <p className={classes.webAuthorAbout}>{article.authorAbout}</p>
                  )}
                </div>
              </div>
              <span className={classes.webFollow} aria-hidden="true">
                <Plus size={14} />
                {t('editor.previewWindow.follow')}
              </span>
            </div>
            <div className={classes.webMeta}>
              <p>
                {t('editor.previewWindow.web.meta', {
                  minutes: webMinutes(article.blocks),
                  date: webDate(article.date),
                })}
              </p>
              <span className={classes.webActions} aria-hidden="true">
                <span>
                  <Moon size={16} />0
                </span>
                <span>
                  <MessageCircle size={16} />0
                </span>
                <i className={classes.webActionsRule} />
                <Bookmark size={20} className={classes.webActionIcon} />
                <span className={classes.webActionIcon}>
                  <Glyph name="share" size={20} />
                </span>
              </span>
            </div>
          </div>

          <div className={classes.webBody}>
            {article.blocks.map((block) => (
              <WebBlock key={block.blockId} block={block} articleId={article.articleId} />
            ))}
          </div>

          <div className={classes.webComments} aria-hidden="true">
            <p className={classes.webCommentsTitle}>{t('editor.previewWindow.web.responses')}</p>
            <div className={classes.webCommentBox}>{t('editor.previewWindow.thoughts')}</div>
            <div className={classes.webCommentSend}>
              <span>{t('editor.previewWindow.web.respond')}</span>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

function WebBlock({
  block,
  articleId,
}: {
  readonly block: Block;
  readonly articleId: string;
}): ReactNode {
  switch (block.kind) {
    case 'text':
    case 'quote':
      return <ReadOnlyText block={block} classNames={WEB_TEXT} />;
    case 'image':
      // At the picture's own proportions, the column's width; no caption.
      return block.url === undefined ? null : (
        <div className={classes.webImageFrame}>
          <BodyImage block={block} className={classes.webImage} />
        </div>
      );
    case 'divider':
      return <SparkleDivider className={classes.webDivider} />;
    case 'video':
      return (
        <div className={classes.webImageFrame}>
          <BlockVideoPlayer articleId={articleId} blockId={block.blockId} video={block.video} />
        </div>
      );
    default:
      return null;
  }
}

/* --------------------------------------------------------------------------------- the app */

const APP_TEXT: ReadOnlyTextClasses = {
  text: classes.appText,
  quote: classes.appQuote,
  quoteText: classes.appQuoteText,
  attribution: classes.appAttribution,
};

/** The app's date: "Oct 05, 2026" - its "MMM dd yyyy" template, the day padded. */
function appDate(date: Date): string {
  return new Intl.DateTimeFormat(CABINET_LOCALE, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(date);
}

/** The app's reading time: the text's characters / 850, rounded up, at least one. */
function appMinutes(blocks: readonly Block[]): number {
  const characters = blocks.reduce(
    (sum, block) => sum + (block.kind === 'text' ? block.body.length : 0),
    0,
  );
  return Math.max(1, Math.ceil(characters / 850));
}

/** The topic pill: its name and a plus, outlined - drawn over the title and again after the body. */
function TopicPill({ name }: { readonly name: string }) {
  return (
    <span className={classes.appTopic}>
      {name}
      <span className={classes.appTopicPlus} aria-hidden="true">
        <Plus size={14} strokeWidth={1.5} />
      </span>
    </span>
  );
}

/** The like (a crescent), comment, save and share marks - the row after the body and the pill. */
function AppActions({ className }: { readonly className: string | undefined }) {
  return (
    <span className={className} aria-hidden="true">
      <Moon size={19} />
      <MessageCircle size={18} />
      <Bookmark size={18} />
      <Glyph name="share" size={19} />
    </span>
  );
}

function AppArticle({ article }: { readonly article: PreviewArticle }) {
  const { t } = useTranslation('articles');
  const minutes = appMinutes(article.blocks);
  return (
    <section className={classes.phone} aria-label={t('editor.previewWindow.inApp')}>
      <div className={classes.phoneScreen}>
        <div className={classes.phoneStatus} aria-hidden="true">
          <span>9:41</span>
          <span className={classes.phoneIsland} />
          <span className={classes.phoneSignal} />
        </div>
        <div className={classes.appNav} aria-hidden="true">
          <ChevronLeft size={26} strokeWidth={2.2} />
          <span className={classes.appMore}>
            <i />
            <i />
            <i />
          </span>
        </div>
        <div className={classes.appScroll}>
          <Starfield className={classes.appStars} width={340} height={140} count={47} />
          <div className={classes.appContent}>
            <div className={classes.appHeader}>
              <span className={classes.appMember}>
                <svg
                  className={classes.appCrown}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M3 7l4.5 4L12 4l4.5 7L21 7l-2 11H5L3 7z" fill="currentColor" />
                </svg>
                {t('editor.previewWindow.app.member')}
              </span>
              {article.category === null ? null : (
                <div className={classes.appTopics}>
                  <TopicPill name={article.category} />
                </div>
              )}
              <h1 className={classes.appTitle}>
                {article.title === '' ? t('common.untitled') : article.title}
              </h1>
              {article.subtitle === '' ? null : (
                <p className={classes.appSubtitle}>{article.subtitle}</p>
              )}
              <p className={classes.appReading}>
                {t('editor.previewWindow.app.reading', { minutes, date: appDate(article.date) })}
              </p>
            </div>
            <div className={classes.appByline}>
              <AuthorPhoto article={article} className={classes.appAvatar} />
              <span className={classes.appName}>{article.authorName}</span>
              <span className={classes.appFollow} aria-hidden="true">
                {t('editor.previewWindow.follow')}
              </span>
            </div>
            {article.blocks.map((block) => (
              <AppBlock key={block.blockId} block={block} articleId={article.articleId} />
            ))}
            {article.category === null ? null : (
              <div className={classes.appTopics}>
                <TopicPill name={article.category} />
              </div>
            )}
            <AppActions className={classes.appEngagement} />
          </div>
          <div className={classes.appWrittenBy}>
            <AuthorPhoto article={article} className={classes.appWrittenByPhoto} />
            <p className={classes.appWrittenByName}>
              {t('editor.previewWindow.app.writtenBy', { name: article.authorName })}
            </p>
            {article.authorAbout === null ? null : (
              <p className={classes.appWrittenByAbout}>{article.authorAbout}</p>
            )}
            <span className={classes.appFollowFilled} aria-hidden="true">
              {t('editor.previewWindow.follow')}
            </span>
          </div>
          <div className={classes.appComments} aria-hidden="true">
            <p className={classes.appCommentsTitle}>{t('editor.previewWindow.app.comments')}</p>
            <div className={classes.appCommentBox}>{t('editor.previewWindow.thoughts')}</div>
          </div>
        </div>
        <AppActions className={classes.appPill} />
      </div>
    </section>
  );
}

function AppBlock({
  block,
  articleId,
}: {
  readonly block: Block;
  readonly articleId: string;
}): ReactNode {
  switch (block.kind) {
    case 'text':
    case 'quote':
      return <ReadOnlyText block={block} classNames={APP_TEXT} />;
    case 'image': {
      if (block.url === undefined) {
        return null;
      }
      const caption = block.caption?.trim() ?? '';
      return (
        <figure className={classes.appFigure}>
          <BodyImage block={block} className={classes.appImage} />
          {caption === '' ? null : <figcaption>{caption}</figcaption>}
        </figure>
      );
    }
    case 'divider':
      return (
        <p className={classes.appDivider} aria-hidden="true">
          • • •
        </p>
      );
    case 'video':
      return (
        <figure className={classes.appFigure}>
          <BlockVideoPlayer articleId={articleId} blockId={block.blockId} video={block.video} />
        </figure>
      );
    default:
      return null;
  }
}
